import { useEffect } from 'react';
import { fittingAt, setFittingAt, useBook } from './assemblyStore';
import { gestureMoved, useAssemblySlots, useDocTools, useFittingResolver } from './assemblySlots';
import { useDrawableParts } from './assemblyClip';
import { changedSince, marksOfOtherParts, marksRenewed } from './fittingModels';

// A SIGN-OFF IS WITHDRAWN WHEN WHAT IT WAS GIVEN ON IS NO LONGER WHAT IS DRAWN — mounted with
// the open manual (NoticeViewer), so no phase has to be open for it.
//
// A movement is numbers about two meshes. One of them re-made, turned, rescaled or bored is
// another mesh under the same numbers, and the tick on it says somebody watched a picture that
// no longer exists. Three ways that happens, and all end here:
//   - a PART it draws has another model than the one it was signed off on (see changedSince);
//   - a PIECE it stands on is no longer made of the models it was signed off on — which takes
//     the piece's model away (see useDrawableParts) and so every movement drawn on it;
//   - PHASE 4 has changed the GESTURE under it (see gestureMoved): the answer is about another
//     sub-step, left at this one's position by a re-run that cut the step differently.
// The second is what carries it down the manual: the sub-step that prepared a screw loses its
// tick with the screw, its piece goes with it, and the sub-step that drives that piece into a
// frame loses its own on the next pass.
//
// ONLY ON EVIDENCE. Before the Parts DB and the sub-steps' pieces have answered, every part
// reads as having no model, and a tick withdrawn on that would be withdrawn for good.
export function SignOffSync({ docId }: { docId: string }) {
  const book = useBook(docId);
  const slots = useAssemblySlots(docId);
  const tools = useDocTools(docId);
  const { modelOf, loaded, stalePieces } = useDrawableParts(docId);
  // The parts' catalog rows: before them, every part of a sub-step reads as unnamed.
  const { loaded: partsResolved } = useFittingResolver(docId);
  useEffect(() => {
    if (!loaded) return;
    for (const slot of slots) {
      // A repeat holds no fitting of its own: its leader's is the one judged.
      if (slot.repeat && slot.repeat.leader !== slot.key) continue;
      const f = fittingAt(book, slot.key);
      const toolItemId = tools.find((tool) => tool.value === f.toolId)?.itemId ?? null;
      const onStale = [f.targetId, f.movingId].some((id) => id != null && stalePieces.has(id));
      if (f.validated && partsResolved && gestureMoved(slot, f)) {
        setFittingAt(docId, slot.key, { validated: false, withdrawnFor: 'gesture', signedOn: undefined, signedFor: undefined });
        continue;
      }
      // AND WHAT A WITHDRAWN SIGN-OFF WAS GIVEN ON, where it was given on other parts: a trace of
      // another gesture, which the line under the viewer would read as another 3D model.
      if (!f.validated && marksOfOtherParts(f.signedOn, f, modelOf, toolItemId)) {
        setFittingAt(docId, slot.key, { validated: false, signedOn: undefined, signedFor: undefined });
        continue;
      }
      if (f.validated && (onStale || changedSince(f.signedOn, f, modelOf, toolItemId).length)) {
        setFittingAt(docId, slot.key, { validated: false });
        continue;
      }
      // MARKS KEPT THE OLD WAY, STILL TRUE, ARE WRITTEN AGAIN AS TODAY'S (see marksRenewed) —
      // the tick's and the rule's. Nothing else about the fitting moves, the tick least of all.
      const signedOn = f.validated ? marksRenewed(f.signedOn, f, modelOf, toolItemId) : null;
      const placedOn = onStale ? null : marksRenewed(f.placedOn, f, modelOf, toolItemId);
      if (signedOn || placedOn) {
        setFittingAt(docId, slot.key, {
          validated: f.validated,
          ...(signedOn ? { signedOn } : {}),
          ...(placedOn ? { placedOn, placedBy: f.placedBy } : {}),
        });
      }
    }
  }, [docId, book, slots, tools, modelOf, loaded, stalePieces, partsResolved]);
  return null;
}
